import { apiFetch } from "@/lib/apiClient";

// Store invites + member management client (TNK-80). All endpoints are dark
// unless MULTI_USER_ENABLED on the server (they 404 when off).

export type StoreRole = "owner" | "manager" | "staff";
export type InviteRole = "manager" | "staff";

export interface InviteInfo {
  valid: boolean;
  storeName?: string;
  role?: InviteRole;
  error?: string;
}

export interface MemberRow {
  userId: string;
  role: StoreRole;
  displayName: string;
  ebayLinked: boolean;
  isSelf: boolean;
  createdAt: string;
}

async function errorMessage(res: Response, fallback: string): Promise<string> {
  const body = (await res.json().catch(() => ({}))) as { error?: string };
  return body.error ?? fallback;
}

// Public: returns whether MULTI_USER_ENABLED so the UI can show/hide the
// multi-user surfaces without baking the flag into the static build.
export async function fetchConfig(): Promise<{ multiUserEnabled: boolean }> {
  const res = await apiFetch("/api/config");
  if (!res.ok) return { multiUserEnabled: false };
  return (await res.json()) as { multiUserEnabled: boolean };
}

// Public (no auth): validate + describe an invite for the /join landing page.
export async function validateInvite(token: string): Promise<InviteInfo> {
  const res = await apiFetch(`/api/invites/${encodeURIComponent(token)}`);
  const body = (await res.json().catch(() => ({}))) as InviteInfo;
  if (!res.ok) {
    return {
      valid: false,
      error: body.error ?? "This invite link is invalid.",
    };
  }
  return body;
}

// Owner: mint an invite token for the given role.
export async function createInvite(role: InviteRole): Promise<string> {
  const res = await apiFetch("/api/invites", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ role }),
  });
  if (!res.ok)
    throw new Error(await errorMessage(res, "Could not create invite."));
  const body = (await res.json()) as { token: string };
  return body.token;
}

// Build the shareable join URL for a freshly-minted token.
export function inviteUrl(token: string): string {
  return `${window.location.origin}/join/${token}`;
}

export async function fetchMembers(): Promise<MemberRow[]> {
  const res = await apiFetch("/api/members");
  if (!res.ok)
    throw new Error(await errorMessage(res, "Could not load members."));
  return (await res.json()) as MemberRow[];
}

export async function updateMemberRole(
  userId: string,
  role: StoreRole,
): Promise<void> {
  const res = await apiFetch(`/api/members/${userId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ role }),
  });
  if (!res.ok)
    throw new Error(await errorMessage(res, "Could not change role."));
}

export async function removeMember(userId: string): Promise<void> {
  const res = await apiFetch(`/api/members/${userId}`, { method: "DELETE" });
  if (!res.ok)
    throw new Error(await errorMessage(res, "Could not remove member."));
}
